'use client';

import { useEffect, useState } from 'react';
import { Grid2, Button, Typography, Chip } from '@mui/material';
import { Card, CardHeader } from '@/components/ui/card';
import LoadData from '@/components/load-data';
import { ROText } from '@/components/locale';
import AppleIcon from '@public/images/social/white_apple.png';
import GooglePlayIcon from '@public/images/social/white_google.png';
import FacebookIcon from '@public/svg/facebook';
import InstagramIcon from '@public/svg/instagram';
import LinkedinIcon from '@public/svg/linkedin';
import YouTubeIcon from '@public/svg/youtube';
import TikTokIcon from '@public/svg/tiktok';
import Image from 'next/image';
import useRedirectToApp from '@/lib/hooks/useRedirectToApp';
import { Platform } from '@/lib/utils';
import useWindowSize from '@/lib/hooks/useWindowSize';
import BlogsService from '@/services/public/blogs';
import Head from 'next/head';
import { BASE_URL } from '@/constants/url';
import ShareMenu from './ShareMenu';
import { useRouter } from 'next/navigation';
import { TbUser, TbCalendar, TbTag } from 'react-icons/tb';

const chipNewSx = {
  color: 'var(--white)',
  background: 'var(--primary)',
  marginRight: 2,
  padding: "0px",
  fontSize: "12px",
  height: 24,
  fontWeight: 400
};

export default function BlogPage({ params }: { params: any }) {
  const { slug } = params;
  const router = useRouter();
  const { goToApp } = useRedirectToApp();
  const [width] = useWindowSize();
  const [blogData, setBlogData] = useState<any>(null);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [dataError, setDataError] = useState<string | null>(null);


  async function getBlogDetails() {
    setIsDataLoading(true)
    try {
      const result: any = await BlogsService.getSingleBlog(slug);
      if (result?.data?.success) {
        setBlogData(result?.data?.data[0])
        setDataError(null)
      }
    } catch (err: any) {
      setDataError(err?.response?.data?.message || "We are sorry for the incovenience but we couldn't get the blogs! Please try again later!");
    } finally {
      setIsDataLoading(false)
    }
  }

  useEffect(() => {
    getBlogDetails()
  }, [slug])

  function returnWidth() {
    return (width - 300)
  }

  function returnHeight() {
    return ((width - 300) * 4) / 3
  }

  useEffect(() => {
    if (blogData) {
      if (blogData?.language == "al" && params?.locale == "sq") {
        return
      }
      if (params?.locale != blogData?.language) {
        router.push(`/${blogData?.language == 'al' ? 'sq' : blogData?.language}/blog/${slug}`)
      }
    }
  }, [blogData])

  const getCategoryName = (cat: any) => {
    const langKey = `name_${blogData?.language}` as keyof typeof cat;
    return cat[langKey] || cat.name_en;
  };

  return (
    <>
      {blogData && (
        <Head>
          <style>
            {`img{width: "100%"}`}
          </style>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BlogPosting",
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `${BASE_URL}blog/${blogData?.slug}`
                },
                "headline": blogData?.title,
                "image": `${BASE_URL}/storage${blogData?.cover?.filter(
                  (imgObj: any) => imgObj.key == 'cover_thumbnails_600x400',
                )[0]?.path
                  }`, // URL to cover image
                "datePublished": blogData?.created_at, // ISO format recommended
                "author": {
                  "@type": "Person",
                  "name": blogData?.author
                },
                "publisher": {
                  "@type": "Service",
                  "name": blogData?.author,
                  "logo": {
                    "@type": "ImageObject",
                    "url": `${BASE_URL}images/round_logo.png`
                  }
                },
                "description": blogData?.description,
                "articleBody": blogData?.content, // Optional if content is very long
                "inLanguage": blogData?.language || "en",
                "keywords": blogData?.tags?.join(', '),
                "articleSection": blogData?.categories?.map((cat: any) => cat?.name)
              })
            }}
          />
          <style>
            {`
              img {
                width: 100%;
              }
              .outside-logo {
                width: auto !important;
              }
            `}
          </style>
        </Head>
      )}

      <main className="mx-auto px-4 py-8 my-4">
        {isDataLoading && (
          <LoadData />
        )}
        <Grid2 container justifyContent={'center'} sx={{ padding: 0, gap: 4 }}>
          <Grid2 size={{ lg: 7, md: 9, sm: 12 }} px={{ lg: 4, md: 2, sm: 0, xs: 0 }} >
            <ShareMenu title={blogData?.title} />
            <header className="container mx-0 px-0 md:mx-auto mt-2">
              <div className="flex gap-2 mb-6">
                {blogData?.categories?.map((cat: any) => (
                  <span key={cat.id} className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                    {getCategoryName(cat)}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                {blogData?.title}
              </h1>

              <p className="text-md md:text-xl text-gray-500 mb-1 md:mb-8 italic">
                {blogData?.description}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-gray-400 border-b border-gray-100 pb-2 md:pb-8">
                <div className="flex items-center gap-2">
                  <TbUser size={18} />
                  <span className="text-sm font-medium">{blogData?.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <TbCalendar size={18} />
                  <span className="text-sm">{blogData?.created_at?.substring(0, 16).replace("T", " ")}</span>
                </div>
              </div>
            </header>
            <Image
              src={`${BASE_URL}/storage${blogData?.cover?.filter(
                (imgObj: any) => imgObj.key == 'cover',
              )[0]?.path
                }`}
              width={returnWidth()}
              height={returnHeight()}
              alt={blogData?.alt ?? blogData?.title}
              title={blogData?.alt ?? blogData?.title}
              className={`object-cover my-4`}
              key={blogData?.id}
              loading="lazy"
              style={{ marginBottom: 20, width: "100%", height: "auto" }}
              // quality="85"
              onLoadingComplete={(img: any) => console.log("Image loaded successfully")}
            />
            {blogData?.content ? (
              <div style={{ textAlign: "justify" }}
                dangerouslySetInnerHTML={{
                  __html: `
                  <style>
                    img {
                     
                      border-radius: 18px;
                    }

                     span, p, li {
                      font-size: 18px
                     }
                  </style>
                  ${blogData?.content}
                `,
                }} />
            ) : (
              <></>
            )}
            <div className='flex flex-col justify-between flex-1 gap-1 mt-4'>
              <div className="flex flex-wrap gap-3 mb-8">
                {blogData?.tags.map((tag: string, i: number) => (
                  <div key={i} className="flex items-center gap-1 text-gray-500 bg-white-mode px-3 py-1 rounded-md text-sm">
                    <TbTag size={14} />
                    {tag}
                  </div>
                ))}
              </div>
            </div>
          </Grid2>
          <Grid2 size={{ lg: 7, md: 9, sm: 11, xs: 12 }} className="flex mobile-flex gap-2 justify-center" px={{ lg: 4, md: 2, sm: 0, xs: 0 }} >
            <Card
              className='mt-8 flex flex-col bg-white-mode md:flex-row items-center md:items-center py-4 space-y-2 md:space-y-0 md:space-x-2 border rounded-lg'
            >
              <div className='flex flex-col justify-between flex-1 gap-1 mt-4 px-4 md:px-10'>
                <Typography className='text-md font-bold text-start mt-2' style={{ fontSize: "17px" }}>
                  <ROText i18n="download_the_app" />
                </Typography>
                <div className='flex py-4 gap-4'>
                  <Button
                    disableElevation
                    onClick={() => goToApp(Platform.Android)}
                    size='medium'
                    variant='outlined'
                    className='normal-case text-white font-bold bg-dark rounded-lg border-none'
                  >
                    <Image src={GooglePlayIcon} alt='google_play' width={25} className='pe-1' style={{ transform: 'scale(0.8)', maxWidth: '50px' }} />
                    <span className='min-w-fit'>Google Play</span>
                  </Button>
                  <Button
                    disableElevation
                    onClick={() => goToApp(Platform.Ios)}
                    size='medium'
                    variant='outlined'
                    className='normal-case text-white font-bold bg-dark rounded-lg border-none'
                  >
                    <Image src={AppleIcon} alt='apple' width={25} className='pe-1' style={{ transform: 'scale(0.8)', maxWidth: '50px' }} />
                    <span className='min-w-fit'>App Store</span>
                  </Button>
                </div>
              </div>
            </Card>
            <Card
              className='flex flex-col mt-8 bg-white-mode md:flex-row items-center md:items-center py-4 space-y-2 md:space-y-0 md:space-x-2 border rounded-lg'
            >
              <div className='flex flex-col justify-between flex-1 gap-2 mt-4 px-20'>
                <CardHeader className='p-0'>
                  <Typography className='text-md font-bold text-start mt-2' style={{ fontSize: "17px" }}>
                    <ROText i18n="follow_us_posts" />
                  </Typography>
                </CardHeader>
              </div>
              <div className='flex mt-4 gap-3'>
                <div
                  className='text-center ps-3 pe-2 py-2 cursor-pointer hover:opacity-70'
                  onClick={() => {
                    window.open('https://www.facebook.com/share/1HusRwiJDe/?mibextid=wwXlfr', '_blank');
                  }}
                >
                  <FacebookIcon color='var(--text-primary)' width={30} height={30} />
                </div>
                <div
                  className='text-center ps-2 pe-2 py-2 cursor-pointer hover:opacity-70'
                  onClick={() => {
                    window.open('https://www.instagram.com/rentout.ai/', '_blank');
                  }}
                >
                  <InstagramIcon color='var(--text-primary)' width={30} height={30} />
                </div>
                <div
                  className='text-center ps-3 pe-2 py-2 cursor-pointer hover:opacity-70'
                  onClick={() => {
                    window.open('https://www.linkedin.com/company/rentout-ai/', '_blank');
                  }}
                >
                  <LinkedinIcon color='var(--text-primary)' width={30} height={30} aria-label="linkedin" />
                </div>
                <div
                  className='text-center ps-2 pe-2 py-2 cursor-pointer hover:opacity-70'
                  onClick={() => {
                    window.open('https://www.youtube.com/@RENTOUT-APP', '_blank');
                  }}
                >
                  <YouTubeIcon color='var(--text-primary)' width={30} height={30} aria-label="linkedin" />
                </div>
                <div
                  className='text-center ps-2 pe-2 py-2 cursor-pointer hover:opacity-70'
                  onClick={() => {
                    window.open('https://www.tiktok.com/@rentout.ai', '_blank');
                  }}
                >
                  <TikTokIcon color='var(--text-primary)' width={40} height={40} aria-label="linkedin" />
                </div>
              </div>
            </Card>
          </Grid2>
        </Grid2>
      </main>
    </>
  );
}
